import React from "react";
import ForgeShell, { ControlLabel, Segmented, Chips, Textarea, OutputCard, CopyableText, EmptyState } from "./ForgeShell";
import { useForgeModule, GenerateButton, ActionRow, DraftsList } from "./useForgeModule";

const PLATFORMS = [
  { value: "meta", label: "Meta" },
  { value: "instagram", label: "Instagram" },
  { value: "tiktok", label: "TikTok" },
  { value: "google", label: "Google" },
  { value: "youtube", label: "YouTube" },
];
const ANGLES = ["Emotional", "Authority", "Curiosity", "Pain-point", "Luxury", "Urgency"];

export default function AdCreative() {
  const m = useForgeModule("ad");
  const i = m.inputs;
  const set = (k, v) => m.setInputs({ ...i, [k]: v });

  return (
    <ForgeShell
      testid="forge-ad-creative"
      left={
        <>
          <ControlLabel>Platform</ControlLabel>
          <Segmented testid="ad-platform" options={PLATFORMS} value={i.platform || "meta"} onChange={(v) => set("platform", v)} />

          <ControlLabel hint="Lead the hook with one of these">Creative angle</ControlLabel>
          <Chips testid="ad-angle" options={ANGLES} value={i.angle || "Curiosity"} onChange={(v) => set("angle", v)} />

          <ControlLabel>Product / outcome</ControlLabel>
          <Textarea testid="ad-product" value={i.product} onChange={(v) => set("product", v)} rows={3}
            placeholder="$99 Creator Growth Playbook — 90-day revenue system" />

          <ControlLabel>Target</ControlLabel>
          <Textarea testid="ad-target" value={i.target} onChange={(v) => set("target", v)} rows={2}
            placeholder="Solo creators 25–40, stuck reactive on Meta" />

          <GenerateButton testid="ad-generate" loading={m.loading} onClick={m.generate} />
        </>
      }
      center={
        !m.output ? (
          <EmptyState title="Spin up an ad" body="Hook · structure · CTA · UGC brief · thumbnail concept · 3 alternate angles." />
        ) : (
          <>
            <ActionRow testid="ad-actions" onRegenerate={m.generate} onSave={() => m.saveDraft(m.output.primary_hook)} regenerating={m.loading} />

            <OutputCard title="Primary hook"><CopyableText testid="ad-hook" text={m.output.primary_hook} /></OutputCard>
            <OutputCard title="Emotional angle"><CopyableText testid="ad-emotion" text={m.output.emotional_angle} /></OutputCard>

            <OutputCard title="Ad structure">
              <ol className="space-y-2">
                {(m.output.ad_structure || []).map((line, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm" data-testid={`ad-structure-${idx}`}>
                    <span className="h-5 w-5 rounded-full bg-ai-soft text-ai text-[11px] font-bold grid place-items-center shrink-0">{idx + 1}</span>
                    <p className="text-ink flex-1">{line}</p>
                  </li>
                ))}
              </ol>
            </OutputCard>

            <div className="grid md:grid-cols-2 gap-4">
              <OutputCard title="CTA"><CopyableText testid="ad-cta" text={m.output.cta} /></OutputCard>
              <OutputCard title="Thumbnail concept"><CopyableText testid="ad-thumbnail" text={m.output.thumbnail_concept} /></OutputCard>
            </div>

            <OutputCard title="UGC brief"><CopyableText testid="ad-ugc" text={m.output.ugc_prompt} /></OutputCard>

            <OutputCard title="Alternate angles">
              <ul className="space-y-2.5">
                {(m.output.alt_variants || []).map((v, idx) => (
                  <li key={idx} className="rounded-xl border border-line bg-zinc-50 p-3" data-testid={`ad-alt-${idx}`}>
                    <p className="text-[10px] uppercase tracking-wider text-meta font-bold">{v.angle}</p>
                    <p className="text-sm text-ink mt-1.5">{v.hook}</p>
                  </li>
                ))}
              </ul>
            </OutputCard>
          </>
        )
      }
      right={
        <>
          <div>
            <ControlLabel>Angle types</ControlLabel>
            <ul className="mt-2 space-y-1 text-[11px] text-ink-soft">
              <li><span className="text-ai font-semibold">Emotional</span> · pulls a feeling first</li>
              <li><span className="text-ai font-semibold">Authority</span> · positions you as the operator</li>
              <li><span className="text-ai font-semibold">Curiosity</span> · creates an open loop</li>
              <li><span className="text-ai font-semibold">Pain-point</span> · names the friction</li>
              <li><span className="text-ai font-semibold">Luxury</span> · status & access</li>
              <li><span className="text-ai font-semibold">Urgency</span> · time-bound action</li>
            </ul>
          </div>
          <DraftsList drafts={m.drafts} onLoad={m.loadDraft} onDelete={m.deleteDraft}
            onToggleFavorite={m.toggleFavorite} onArchive={m.archiveDraft}
            onDuplicate={m.duplicateDraft} module="ad" />
        </>
      }
    />
  );
}
